import { Component, Input, OnInit, OnChanges, SimpleChanges, ChangeDetectorRef, DestroyRef, inject} from '@angular/core'
import { takeUntilDestroyed } from '@angular/core/rxjs-interop'
import { AbstractControl, FormGroup, Validators } from '@angular/forms'
import { Country } from '@app/models/country.model'
import { CountryCodeValidator, MobileValidator } from '@app/utils/validators'
import { DynamicFormControlValidatorService } from '../../services/dynamic-form-control-validator.service'
import { CountriesService } from '../../pages/settings/services/countries.service'
import { DetailsService } from '../../pages/settings/services/details.service'
import { take } from 'rxjs'

@Component({
  selector: 'app-mobile-with-country-code-input',
  templateUrl: './mobile-with-country-code-input.component.html',
  styleUrls: ['./mobile-with-country-code-input.component.scss'],
  standalone: false,
})
export class MobileWithCountryCodeInputComponent implements OnInit, OnChanges {
  private destroyRef = inject(DestroyRef)
  /** Form group */
  @Input() form: FormGroup

  /** Input class */
  @Input() inputClass: string

  /** Is required */
  @Input() required: boolean = true

  /** Is equal inputs width? */
  @Input() equalSize: boolean

  /** Use form group class (contains margin bottom) by default is true */
  @Input() useFormGroupClass: boolean = true

  /** Read only input? */
  @Input() readOnly: boolean

  /** Country code key by default is 'country_code'*/
  @Input() countryCodeKey: string = 'country_code'

  /** Mobile key by default is 'mobile' */
  @Input() mobileKey: string = 'mobile'

  /** Set default code by client  */
  @Input() setDefaultCodeByClient: boolean

  /** Disabled input */
  @Input() disabled: boolean

  /** Country code input id */
  @Input() countryInputId: string

  /** Country code input id */
  @Input() mobileInputId: string

  /** Input tab index */
  @Input() tabindex: string | number | null = null

  /** Countries array */
  countries: Country[] = []


  constructor(
    private countryService: CountriesService,
    private formValidatorService: DynamicFormControlValidatorService,
    private detailsService: DetailsService,
    private cdr: ChangeDetectorRef
  ) {}

  ngOnInit(): void {
    if (!this.inputClass) this.inputClass = 'p-input-filled-light'
    if (!this.countryInputId) this.countryInputId = this.countryCodeKey
    if (!this.mobileInputId) this.countryInputId = this.mobileKey

    // Load countries
    this.countryService
      .getCountries()
      .pipe(take(1))
      .subscribe((countries) => {
        this.countries = countries
        this.cdr.detectChanges()
      })

    this.formValidatorService.set(this.form.get(this.countryCodeKey) as AbstractControl, [
      [Validators.required, CountryCodeValidator(this.form.get(this.mobileKey) as AbstractControl)],
      [CountryCodeValidator(this.form.get(this.mobileKey) as AbstractControl)],
    ])
    this.formValidatorService.set(this.form.get(this.mobileKey) as AbstractControl, [
      [MobileValidator(this.form.get(this.countryCodeKey) as AbstractControl)],
      [MobileValidator(this.form.get(this.countryCodeKey) as AbstractControl)],
    ])

    this.setCountryCodeValue()
    if (this.required) this.formValidatorService.switchFormValidator()

    // Handle initial disabled state
    this.updateDisabledState()
    this.cdr.detectChanges()

    // Subscribe to value changes to trigger cross-validation
    this.form
      .get(this.mobileKey)!
      .valueChanges.pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((v) => {
        if (!this.required) {
          if (v?.length) {
            this.form.get(this.mobileKey)!.addValidators([Validators.required, MobileValidator(this.form.get(this.countryCodeKey) as AbstractControl)])
          } else {
            this.form.get(this.mobileKey)!.setErrors(null)
            this.form.get(this.countryCodeKey)!.setErrors(null)
          }
        }
        // Trigger country code validation when mobile changes
        this.form.get(this.countryCodeKey)!.updateValueAndValidity({ emitEvent: false })
      })

    // Subscribe to country code changes to trigger mobile validation
    this.form
      .get(this.countryCodeKey)!
      .valueChanges.pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        // Trigger mobile validation when country code changes
        this.form.get(this.mobileKey)!.updateValueAndValidity({ emitEvent: false })
      })
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['disabled']) {
      this.updateDisabledState()
    }
  }

  private updateDisabledState(): void {
    if (this.form) {
      const countryControl = this.form.get(this.countryCodeKey)
      const mobileControl = this.form.get(this.mobileKey)

      if (countryControl) {
        if (this.disabled) {
          countryControl.disable({ emitEvent: false })
        } else {
          countryControl.enable({ emitEvent: false })
        }
      }

      if (mobileControl) {
        if (this.disabled) {
          mobileControl.disable({ emitEvent: false })
        } else {
          mobileControl.enable({ emitEvent: false })
        }
      }
    }
  }


  get dir() {
    return document.dir
  }

  /**
   * Is field is required
   * @param key Form key
   * @returns True/false
   */
  isRequired(key: string): boolean {
    if (this.required) {
      const control: AbstractControl | null = this.form?.get(key)
      if (!control) {
        return false
      }
      return !!(control.validator && !control.valid && !control.disabled)
    }
    return false
  }

  /**
   *Set country code value
   */
  setCountryCodeValue() {
    if (this.setDefaultCodeByClient) {
      this.detailsService
        .getCurrentClient()
        .pipe(take(1))
        .subscribe((client) => {
          if (client?.country_code) {
            this.form.get(this.countryCodeKey)!.setValue(client.country_code)
          }
          this.applyRequiredValidator()
        })
    } else {
      // If not setting default, just handle required validators
      this.applyRequiredValidator()
    }
  }

  /**
   * Apply (or clear) the required rule on the mobile control and recompute its validity.
   * `addValidators` alone does NOT recompute status, so an untouched/empty required field stays
   * stale-`valid` — letting parent forms save without a mobile and hiding the required marker.
   * Calling `updateValueAndValidity` makes the empty value compute as invalid immediately.
   */
  private applyRequiredValidator() {
    if (this.required) {
      this.form.get(this.mobileKey)!.addValidators(Validators.required)
      this.form.get(this.mobileKey)!.updateValueAndValidity({ emitEvent: false })
    } else {
      this.form.get(this.mobileKey)!.setErrors(null)
      this.form.get(this.countryCodeKey)!.setErrors(null)
    }
  }
}
